import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router';
import Button from '@mui/material/Button';
import { Box } from '@mui/system';
import { GridCallbackDetails, GridPaginationModel } from '@mui/x-data-grid';

import { QueryRenderer } from '@barboxx/admin-components';
import { usePaging, useSearchString } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Can, useBool } from '@barboxx/react-utils';

import {
    CustomPageHeader,
    RootContainer,
} from '../../components/nav/RootContainer';
import { CreateDialog } from './component/CreateDialog';
import { StandardDrinkTable } from './component/StandardDrinkTable';

export function StandardDrinksList() {
    const { page, size, updatePageModel, sort, updateSort } = usePaging();
    const navigate = useNavigate();
    const [createOpen, createActions] = useBool();

    const { InputComponent, searchString } = useSearchString();

    const standardDrinkQuery = useQuery({
        queryKey: ['standardDrinks', page, size, sort, searchString],
        queryFn: () =>
            Api.StandardDrinks.StandardDrink.getStandardDrinks({
                page,
                size,
                sort,
                searchString,
            }),
    });

    const handleChangePageModel = (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => {
        updatePageModel(model);
    };

    const handleRowClick = (id: string) => {
        navigate(`${id}`);
    };

    const handleCreate = React.useCallback(() => {
        createActions.on();
    }, [createActions]);

    const handleSuccess = () => {
        standardDrinkQuery.refetch();
    };

    const CustomPageToolbarComponent = React.useCallback(
        () => (
            <CustomPageHeader>
                <Box gap={2} flexDirection="row" display="flex">
                    <InputComponent />
                    <Can I="create" a="Product">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                        >
                            Add StandardDrink
                        </Button>
                    </Can>
                </Box>
            </CustomPageHeader>
        ),
        [InputComponent, handleCreate]
    );

    return (
        <RootContainer
            maxWidth="md"
            slots={{ header: CustomPageToolbarComponent }}
        >
            <QueryRenderer results={standardDrinkQuery}>
                {data => (
                    <StandardDrinkTable
                        data={data?.results ?? []}
                        size={size}
                        handleChangePageModel={handleChangePageModel}
                        onRowClick={handleRowClick}
                        onChangeSort={updateSort}
                        page={page}
                        total={data?.total ?? 0}
                    />
                )}
            </QueryRenderer>

            <CreateDialog
                open={createOpen}
                onClose={createActions.off}
                onSuccess={handleSuccess}
            />
        </RootContainer>
    );
}
